import { DownloadOutlined, UploadOutlined } from "@ant-design/icons";
import { Button, notification, Space } from "antd";
import { ImportUserFarmRow, useImportUserFarmsMutation } from "api";
import { ChangeEvent, useRef, useState } from "react";
import * as XLSX from "xlsx";

const EXCEL_COLUMNS = {
	username: "UserID",
	farmCode: "Nhà",
	startDate: "Từ ngày",
	endDate: "Đến ngày",
	status: "Trạng thái",
} as const;

const normalizeText = (value: unknown) => String(value ?? "").trim();

const pad = (value: number) => String(value).padStart(2, "0");

// Ô ngày Excel là số serial; đổi qua SSF để không lệch múi giờ. Ô text nhận yyyy-mm-dd hoặc dd/mm/yyyy, còn lại gửi nguyên để backend báo lỗi
const parseExcelDate = (value: unknown) => {
	if (typeof value === "number") {
		const date = XLSX.SSF.parse_date_code(value);
		return date ? `${date.y}-${pad(date.m)}-${pad(date.d)}` : String(value);
	}

	const text = normalizeText(value);
	const match = text.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);

	return match ? `${match[3]}-${pad(Number(match[2]))}-${pad(Number(match[1]))}` : text;
};

// Giá trị cố ý là giả để import nhầm file mẫu chỉ báo lỗi "không tìm thấy", không phân quyền nhầm user thật
const TEMPLATE_SAMPLE_ROWS = [
	["vidu_user01", "VD001", "2026-01-01", "2026-12-31", "Active"],
	["vidu_user01", "VD002", "2026-01-01", "2026-12-31", "Active"],
	["vidu_user02", "VD001", "2026-10-01", "2027-03-31", "Active"],
	["vidu_user02", "VD003", "2026-01-01", "2026-09-30", "Inactive"],
];

const TEMPLATE_GUIDE = [
	"Chỉ sheet đầu tiên (PhanQuyen) được import. Giữ nguyên dòng tiêu đề, không đổi tên cột.",
	"Các dòng trong sheet PhanQuyen là ví dụ: xóa đi hoặc thay bằng username và mã trại thật trước khi import.",
	"UserID: username đăng nhập (cột Username trên trang User Management), user phải đang active.",
	"Nhà: mã trại (farm_code), trại phải đang hoạt động.",
	"Từ ngày / Đến ngày: ô kiểu ngày, hoặc gõ chữ dạng dd/mm/yyyy hay yyyy-mm-dd. Từ ngày phải ≤ Đến ngày.",
	"Trạng thái: Active hoặc Inactive (không phân biệt hoa thường). Inactive = thu hồi quyền.",
	"User đã có quyền ở trại đó thì cập nhật ngày và trạng thái; chưa có thì tạo mới.",
	"Mỗi cặp UserID + Nhà chỉ được xuất hiện 1 lần trong file; dòng lỗi được báo kèm số dòng, các dòng khác vẫn import.",
];

// Số serial ngày của Excel, tính theo UTC để không phụ thuộc múi giờ máy
const toExcelSerial = (date: string) => Date.parse(`${date}T00:00:00Z`) / 86400000 + 25569;

const downloadTemplate = () => {
	const sheet = XLSX.utils.aoa_to_sheet([
		Object.values(EXCEL_COLUMNS),
		...TEMPLATE_SAMPLE_ROWS.map(([username, farmCode, startDate, endDate, status]) => [username, farmCode, toExcelSerial(startDate), toExcelSerial(endDate), status]),
	]);
	TEMPLATE_SAMPLE_ROWS.forEach((_, index) => {
		sheet[`C${index + 2}`].z = "dd/mm/yyyy";
		sheet[`D${index + 2}`].z = "dd/mm/yyyy";
	});
	sheet["!cols"] = [{ wch: 20 }, { wch: 12 }, { wch: 14 }, { wch: 14 }, { wch: 12 }];

	const guideSheet = XLSX.utils.aoa_to_sheet([["Hướng dẫn import phân quyền"], [], ...TEMPLATE_GUIDE.map((line) => [line])]);
	guideSheet["!cols"] = [{ wch: 110 }];

	const workbook = XLSX.utils.book_new();
	XLSX.utils.book_append_sheet(workbook, sheet, "PhanQuyen");
	XLSX.utils.book_append_sheet(workbook, guideSheet, "HuongDan");
	XLSX.writeFile(workbook, "mau-import-phan-quyen.xlsx");
};

export const ImportUserFarmButton = ({ setFailed }: { setFailed: React.Dispatch<React.SetStateAction<string[]>> }) => {
	const inputRef = useRef<HTMLInputElement>(null);
	const [importUserFarms] = useImportUserFarmsMutation();
	const [loading, setLoading] = useState(false);

	const importExcel = async (file: File) => {
		const buffer = await file.arrayBuffer();
		const workbook = XLSX.read(buffer, { type: "array" });
		const sheetName = workbook.SheetNames[0];

		if (!sheetName) {
			throw new Error("File Excel không có dữ liệu.");
		}

		const rows = XLSX.utils.sheet_to_json<Record<string, unknown>>(workbook.Sheets[sheetName], {
			defval: "",
			raw: true,
			// Giữ dòng trống để vị trí trong mảng khớp số dòng Excel (backend báo lỗi theo index + 2 và tự bỏ qua dòng trống)
			blankrows: true,
		});

		if (!rows.length) {
			throw new Error("File Excel không có dữ liệu.");
		}

		const missingColumns = Object.values(EXCEL_COLUMNS).filter((column) => !(column in rows[0]));

		if (missingColumns.length) {
			throw new Error(`Thiếu cột: ${missingColumns.join(", ")}`);
		}

		const payloads: ImportUserFarmRow[] = rows.map((row) => ({
			username: normalizeText(row[EXCEL_COLUMNS.username]),
			farm_code: normalizeText(row[EXCEL_COLUMNS.farmCode]),
			start_date: parseExcelDate(row[EXCEL_COLUMNS.startDate]),
			end_date: parseExcelDate(row[EXCEL_COLUMNS.endDate]),
			status: normalizeText(row[EXCEL_COLUMNS.status]),
		}));

		// Bỏ các dòng trống ở cuối file (Excel hay để lại vùng định dạng thừa)
		while (payloads.length && Object.values(payloads[payloads.length - 1]).every((value) => !value)) {
			payloads.pop();
		}

		if (!payloads.length) {
			throw new Error("Không tìm thấy dòng dữ liệu hợp lệ.");
		}

		return await importUserFarms(payloads).unwrap();
	};

	const handleChange = async (event: ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0];

		if (!file) return;

		setLoading(true);
		setFailed([]);

		try {
			const result = await importExcel(file);

			notification.success({
				message: `Import thành công ${result.success} dòng`,
			});
			if (result.failed.length) {
				setFailed(result.failed);
			}
		} catch (error: any) {
			notification.error({
				message: error?.data?.message || error?.message || "Import thất bại.",
			});
		} finally {
			setLoading(false);
			event.target.value = "";
		}
	};

	return (
		<Space>
			<Button icon={<DownloadOutlined />} onClick={downloadTemplate}>
				Tải file mẫu
			</Button>
			<Button loading={loading} type="primary" ghost icon={<UploadOutlined />} onClick={() => inputRef.current?.click()}>
				Import phân quyền
			</Button>
			<input ref={inputRef} style={{ display: "none" }} type="file" accept=".xlsx,.xls" onChange={handleChange} />
		</Space>
	);
};
